import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import './ControleGastos.css';
import {
  FaPlus,
  FaFileInvoiceDollar,
  FaCheck,
  FaTimes,
  FaEdit,
  FaTrash,
  FaSearch,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaGasPump,
  FaLock,
  FaSortUp,
  FaSortDown,
  FaSyncAlt,
  FaTools,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
  FaHourglassHalf,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaUndo,
  FaHistory,
  FaReply,
} from 'react-icons/fa';
import GastoModal from './GastoModal';
import AnexoViewerModal from './AnexoViewerModal';
import AnexosGaleria from './AnexosGaleria';
import ModalDevolverPrestacao from './ModalDevolverPrestacao';
import TimelineRevisoes from './TimelineRevisoes';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { parseOSTarefa } from '../utils/osTarefa';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatCurrency = (value) => {
  const num = parseFloat(value);
  return isNaN(num)
    ? 'R$ 0,00'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' });
};

const isAutoSupply = (gasto) =>
  (gasto.descricao && gasto.descricao.includes('Abastecimento Cartão')) ||
  (gasto.categoria_nome && gasto.categoria_nome.toLowerCase().includes('combust'));

// Prestação vinda do app do motorista: só essas entram no ciclo de devolução
// (o backend exige origem_app + motorista para poder notificar e receber a correção).
const isPrestacaoApp = (gasto) =>
  Number(gasto?.origem_app) === 1 && !!gasto?.criado_por_id;

// Estado da camada de revisão (independe do status principal).
//   'devolvida' → aguardando correção do motorista
//   'corrigida' → motorista reenviou; voltou para análise
const getRevisao = (gasto) => gasto?.revisao_status || 'nenhuma';

// ─── Mês de referência ─────────────────────────────────────────────────────────
const MONTH_LABELS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

// Extrai "YYYY-MM" da data_vencimento de forma segura (string ISO ou Date).
const getYearMonth = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}`;
  const d = new Date(value);
  if (isNaN(d)) return null;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

const currentYearMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLong = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} de ${y}`;
};

// ─── Constantes de UI ───────────────────────────────────────────────────────────
// "A analisar" = contas que ainda precisam de decisão (sem baixa de pagamento).
// Rejeitada/Cancelada já são desfecho e não entram na contagem.
const STATUS_A_ANALISAR = ['Pendente', 'Aprovada'];

// Status config (badge label + CSS modifier)
const STATUS_CONFIG = {
  Paga:      { label: 'Paga',      mod: 'paga' },
  Aprovada:  { label: 'Aprovada',  mod: 'aprovada' },
  Pendente:  { label: 'Pendente',  mod: 'pendente' },
  Rejeitada: { label: 'Rejeitada', mod: 'rejeitada' },
  Cancelada: { label: 'Cancelada', mod: 'cancelada' },
};

const FILTER_TABS = ['Todos', 'Pendente', 'Aprovada', 'Paga', 'Rejeitada', 'Cancelada'];

const PAGE_SIZE_OPTS = [12, 24, 48];

// ─── Skeleton Row ─────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <tr className="cg-skeleton-row">
      {[40, 180, 120, 80, 70, 80].map((w, i) => (
        <td key={i}>
          <div className="cg-skeleton" style={{ width: w }} />
        </td>
      ))}
    </tr>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ filtered, mesLabel }) {
  return (
    <tr>
      <td colSpan={6}>
        <div className="cg-empty">
          <div className="cg-empty__icon">
            <FaFileInvoiceDollar />
          </div>
          <p className="cg-empty__title">
            {filtered ? 'Nenhum resultado para os filtros aplicados' : `Nenhum gasto em ${mesLabel}`}
          </p>
          <p className="cg-empty__sub">
            {filtered
              ? 'Ajuste a busca, o filtro de status ou troque o mês na barra acima.'
              : 'Troque o mês na barra acima ou clique em "Novo Gasto" para lançar um registro.'}
          </p>
        </div>
      </td>
    </tr>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ControleGastos() {
  const location = useLocation();
  const navigate = useNavigate();
  const highlightIdFromState = location.state?.highlightId;

  // Usuário logado — uma chamada por sessão, com cache em memória
  const currentUser = useCurrentUser();

  const [gastos, setGastos]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [isModalOpen, setIsModalOpen]   = useState(false);
  const [editingGasto, setEditingGasto] = useState(null);
  const [categorias, setCategorias]     = useState([]);
  const [veiculos, setVeiculos]         = useState([]);
  const [anexoParaVer, setAnexoParaVer] = useState(null);

  // Ciclo de revisão: prestação a devolver (modal de motivo) e a ver histórico
  const [devolverAlvo, setDevolverAlvo] = useState(null);
  const [timelineAlvo, setTimelineAlvo] = useState(null);

  // Filtros
  const [termoBusca, setTermoBusca]     = useState('');
  const [filtroStatus, setFiltroStatus] = useState('Todos');
  const [sortDir, setSortDir]           = useState('desc'); // 'asc' | 'desc'
  const [mesRef, setMesRef]             = useState(currentYearMonth); // 'YYYY-MM'

  // Paginação (client-side)
  const [page, setPage]                 = useState(1);
  const [pageSize, setPageSize]         = useState(PAGE_SIZE_OPTS[0]);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (soft = false) => {
    if (soft) setRefreshing(true);
    else setLoading(true);
    try {
      const [gastosRes, categoriasRes, veiculosRes] = await Promise.all([
        api.get('/api/gastos'),
        api.get('/api/gastos/categorias'),
        api.get('/api/vehicles'),
      ]);
      setGastos(gastosRes.data);
      setCategorias(categoriasRes.data);
      setVeiculos(veiculosRes.data.data || []);
    } catch {
      toast.error('Falha ao carregar dados financeiros.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derivados ────────────────────────────────────────────────────────────────
  // Todas as contas do mês selecionado (independe de busca/status).
  const gastosDoMes = useMemo(
    () => gastos.filter((g) => getYearMonth(g.data_vencimento) === mesRef),
    [gastos, mesRef]
  );

  // Lista filtrada + ordenada (ainda sem recorte de página).
  const gastosFiltrados = useMemo(() => {
    let list = gastosDoMes.filter((g) => {
      const textoMatch =
        (g.descricao || '').toLowerCase().includes(termoBusca.toLowerCase()) ||
        (g.categoria_nome || '').toLowerCase().includes(termoBusca.toLowerCase());
      const statusMatch = filtroStatus === 'Todos' || g.status === filtroStatus;
      return textoMatch && statusMatch;
    });

    list = [...list].sort((a, b) => {
      const da = new Date(a.data_vencimento).getTime();
      const db = new Date(b.data_vencimento).getTime();
      return sortDir === 'desc' ? db - da : da - db;
    });

    return list;
  }, [gastosDoMes, termoBusca, filtroStatus, sortDir]);

  // Recorte de página
  const totalPages     = Math.max(1, Math.ceil(gastosFiltrados.length / pageSize));
  const inicio         = (page - 1) * pageSize;
  const gastosPaginados = gastosFiltrados.slice(inicio, inicio + pageSize);

  // Janela de números de página (até 5, centrada na atual)
  const pageWindow = useMemo(() => {
    const win = [];
    let start = Math.max(1, page - 2);
    let end   = Math.min(totalPages, page + 2);
    while (end - start < 4 && (start > 1 || end < totalPages)) {
      if (start > 1) start -= 1;
      else if (end < totalPages) end += 1;
      else break;
    }
    for (let i = start; i <= end; i += 1) win.push(i);
    return win;
  }, [page, totalPages]);

  // Contas "a analisar" em TODO o sistema (sem baixa de pagamento) → chip do topo.
  // Base = `gastos` (geral), independente do mês visível — serve de radar de atraso.
  const pendingCount = useMemo(
    () => gastos.filter((g) => STATUS_A_ANALISAR.includes(g.status)).length,
    [gastos]
  );

  // ── KPIs (sobre a lista filtrada do mês) ─────────────────────────────────────
  const kpis = useMemo(() => {
    const total    = gastosFiltrados.reduce((acc, g) => acc + (parseFloat(g.valor) || 0), 0);
    const pendente = gastosFiltrados.filter((g) => g.status === 'Pendente').length;
    const pago     = gastosFiltrados.filter((g) => g.status === 'Paga').reduce((acc, g) => acc + (parseFloat(g.valor) || 0), 0);
    const auto     = gastosFiltrados.filter(isAutoSupply).length;
    return { total, pendente, pago, auto };
  }, [gastosFiltrados]);

  // Contagem por status DO MÊS (para badges nas tabs)
  const countsByStatus = useMemo(() => {
    const m = {};
    gastosDoMes.forEach((g) => { m[g.status] = (m[g.status] || 0) + 1; });
    return m;
  }, [gastosDoMes]);

  // ── Volta para a página 1 quando muda mês/busca/status/ordem/tamanho ─────────
  useEffect(() => {
    setPage(1);
  }, [mesRef, termoBusca, filtroStatus, sortDir, pageSize]);

  // ── Mantém a página dentro dos limites ao encolher a lista ───────────────────
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // ── Redirect de outra tela: traz o item para o mês/visão certos ──────────────
  useEffect(() => {
    if (!highlightIdFromState || gastos.length === 0) return;
    const alvo = gastos.find((g) => g.id === highlightIdFromState);
    if (!alvo) return;
    const ym = getYearMonth(alvo.data_vencimento);
    if (ym) setMesRef(ym);
    setFiltroStatus('Todos');
    setTermoBusca('');
  }, [highlightIdFromState, gastos]);

  // ── Rola até a linha destacada (pulando para a página onde ela está) ─────────
  useEffect(() => {
    if (!highlightIdFromState) return;
    const idx = gastosFiltrados.findIndex((g) => g.id === highlightIdFromState);
    if (idx === -1) return;
    const targetPage = Math.floor(idx / pageSize) + 1;
    if (page !== targetPage) { setPage(targetPage); return; }
    const el = document.getElementById(`gasto-row-${highlightIdFromState}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('cg-highlight-row');
      setTimeout(() => el.classList.remove('cg-highlight-row'), 3000);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [highlightIdFromState, gastosFiltrados, page, pageSize, navigate, location.pathname]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSaveGasto = useCallback(async (formData, gastoId) => {
    try {
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (gastoId) {
        await api.post(`/api/gastos/${gastoId}`, formData, config);
        toast.success('Gasto atualizado e sincronizado!');
      } else {
        await api.post('/api/gastos', formData, config);
        toast.success('Gasto registrado no Financeiro!');
      }
      setIsModalOpen(false);
      setEditingGasto(null);
      fetchData(true);
    } catch {
      toast.error('Erro ao salvar. Verifique os dados e tente novamente.');
    }
  }, [fetchData]);

  const handleUpdateStatus = useCallback(async (id, status) => {
    if (!window.confirm(`Confirma alteração de status para "${status}"?`)) return;
    try {
      await api.put(`/api/gastos/${id}/status`, { status });
      toast.success(`Status atualizado para ${status}.`);
      fetchData(true);
    } catch {
      toast.error('Erro ao atualizar status.');
    }
  }, [fetchData]);

  // Clique em "Rejeitar": prestação do app abre o fluxo de devolução (motivo +
  // push + correção); gasto que não é do app mantém a rejeição direta de sempre.
  const handleRejeitarClick = useCallback((gasto) => {
    if (isPrestacaoApp(gasto)) {
      setDevolverAlvo(gasto);
    } else {
      handleUpdateStatus(gasto.id, 'Rejeitada');
    }
  }, [handleUpdateStatus]);

  // Pós-devolução bem-sucedida: recarrega a lista para refletir o novo estado.
  const handleDevolvida = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  const handleEditClick = useCallback((gasto) => {
    if (isAutoSupply(gasto)) {
      toast.warning('🔒 Registro automático de Frota. Para editar, use o módulo de Gestão de Abastecimento.');
      return;
    }
    setEditingGasto(gasto);
    setIsModalOpen(true);
  }, []);

  const handleDeleteGasto = useCallback(async (id) => {
    const gasto = gastos.find((g) => g.id === id);
    if (gasto && isAutoSupply(gasto)) {
      toast.error('🔒 Ação Bloqueada: Registro vinculado ao saldo do cartão de abastecimento.');
      return;
    }
    if (!window.confirm('Excluir este lançamento permanentemente?')) return;
    try {
      await api.delete(`/api/gastos/${id}`);
      toast.success('Registro removido com sucesso.');
      fetchData(true);
    } catch {
      toast.error('Erro ao excluir o registro.');
    }
  }, [gastos, fetchData]);

  const handleOpenNew = useCallback(() => {
    setEditingGasto(null);
    setIsModalOpen(true);
  }, []);

  const toggleSort = useCallback(() => {
    setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
  }, []);

  // ── Callback: nova categoria criada inline no modal ──────────────────────────
  const handleCategoriaCreated = useCallback((nova) => {
    setCategorias((prev) =>
      [...prev, nova].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
    );
  }, []);

  const goPrevMonth = useCallback(() => setMesRef((m) => shiftMonth(m, -1)), []);
  const goNextMonth = useCallback(() => setMesRef((m) => shiftMonth(m, 1)), []);
  const goToday     = useCallback(() => setMesRef(currentYearMonth()), []);

  const SortIcon  = sortDir === 'desc' ? FaSortDown : FaSortUp;
  const mesLabel  = formatMonthLong(mesRef);
  const isMesAtual = mesRef === currentYearMonth();

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="cg-root">

      {/* ── PAGE HEADER ─────────────────────────────────────────────────────── */}
      <div className="cg-page-header">
        <div className="cg-page-header__left">
          <div className="cg-page-header__icon">
            <FaFileInvoiceDollar />
          </div>
          <div>
            <h1>Controle de Gastos</h1>
            <p>Lançamento, aprovação e gestão de comprovantes</p>
          </div>
        </div>
        <div className="cg-page-header__actions">
          <button
            className="cg-btn cg-btn--ghost"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            title="Recarregar"
          >
            <FaSyncAlt className={refreshing ? 'cg-spin' : ''} />
          </button>
          <button className="cg-btn cg-btn--primary" onClick={handleOpenNew}>
            <FaPlus /> Novo Gasto
          </button>
        </div>
      </div>

      {/* ── MONTH BAR: navegação de mês + contas a analisar ─────────────────── */}
      <div className="cg-monthbar">
        <div className="cg-month-nav">
          <button className="cg-month-nav__arrow" onClick={goPrevMonth} title="Mês anterior" aria-label="Mês anterior">
            <FaChevronLeft />
          </button>

          <label className="cg-month-nav__label" title="Selecionar mês">
            <FaCalendarAlt />
            <input
              type="month"
              className="cg-month-nav__input"
              value={mesRef}
              max="2100-12"
              onChange={(e) => { if (e.target.value) setMesRef(e.target.value); }}
              aria-label="Mês de referência"
            />
          </label>

          <button className="cg-month-nav__arrow" onClick={goNextMonth} title="Próximo mês" aria-label="Próximo mês">
            <FaChevronRight />
          </button>

          {!isMesAtual && (
            <button className="cg-month-nav__today" onClick={goToday}>
              Ir para o mês atual
            </button>
          )}
        </div>

        <div
          className={`cg-pending-chip ${pendingCount > 0 ? 'is-alert' : 'is-clear'}`}
          title="Total de contas sem baixa de pagamento (pendentes e aprovadas), somando todos os meses"
        >
          <span className="cg-pending-chip__icon">
            {pendingCount > 0 ? <FaHourglassHalf /> : <FaCheck />}
          </span>
          <span className="cg-pending-chip__body">
            <strong>{pendingCount}</strong>
            <span>{pendingCount === 1 ? 'conta a analisar' : 'contas a analisar'}</span>
          </span>
        </div>
      </div>

      {/* ── KPI STRIP ───────────────────────────────────────────────────────── */}
      <div className="cg-kpi-strip">
        <div className="cg-kpi">
          <div className="cg-kpi__icon cg-kpi__icon--blue">
            <FaMoneyBillWave />
          </div>
          <div className="cg-kpi__body">
            <span>Total listado</span>
            <strong>{formatCurrency(kpis.total)}</strong>
          </div>
        </div>

        <div className="cg-kpi">
          <div className="cg-kpi__icon cg-kpi__icon--green">
            <FaCheck />
          </div>
          <div className="cg-kpi__body">
            <span>Total pago</span>
            <strong>{formatCurrency(kpis.pago)}</strong>
          </div>
        </div>

        <div className="cg-kpi">
          <div className="cg-kpi__icon cg-kpi__icon--amber">
            <FaExclamationTriangle />
          </div>
          <div className="cg-kpi__body">
            <span>Pendentes</span>
            <strong>{kpis.pendente}</strong>
          </div>
        </div>

        {kpis.auto > 0 && (
          <div className="cg-kpi">
            <div className="cg-kpi__icon cg-kpi__icon--fuel">
              <FaGasPump />
            </div>
            <div className="cg-kpi__body">
              <span>Abastecimentos</span>
              <strong>{kpis.auto}</strong>
            </div>
          </div>
        )}
      </div>

      {/* ── TOOLBAR ─────────────────────────────────────────────────────────── */}
      <div className="cg-toolbar">
        {/* Busca */}
        <div className="cg-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por descrição ou categoria…"
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
          />
          {termoBusca && (
            <button className="cg-search__clear" onClick={() => setTermoBusca('')}>
              <FaTimes />
            </button>
          )}
        </div>

        {/* Filtros de status */}
        <div className="cg-status-tabs">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab}
              className={`cg-status-tab ${filtroStatus === tab ? 'active' : ''}`}
              onClick={() => setFiltroStatus(tab)}
            >
              {tab}
              {tab !== 'Todos' && countsByStatus[tab] > 0 && (
                <span className="cg-status-tab__count">{countsByStatus[tab]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── TABLE ───────────────────────────────────────────────────────────── */}
      <div className="cg-card">
        <div className="cg-table-wrap">
          <table className="cg-table">
            <thead>
              <tr>
                <th className="col-date">
                  <button className="cg-sort-btn" onClick={toggleSort} title="Ordenar por data">
                    Vencimento <SortIcon className="cg-sort-icon" />
                  </button>
                </th>
                <th>Descrição</th>
                <th className="col-meta">Categoria / Veículo</th>
                <th className="col-val">Valor</th>
                <th className="col-status">Status</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              ) : gastosFiltrados.length === 0 ? (
                <EmptyState
                  filtered={termoBusca !== '' || filtroStatus !== 'Todos'}
                  mesLabel={mesLabel}
                />
              ) : (
                gastosPaginados.map((gasto) => {
                  const auto = isAutoSupply(gasto);
                  const sc   = STATUS_CONFIG[gasto.status] || { label: gasto.status || '—', mod: 'pendente' };

                  // Camada de revisão: sobrepõe o rótulo do badge quando a
                  // prestação está no ciclo de devolução/correção.
                  const revisao     = getRevisao(gasto);
                  const isDevolvida = revisao === 'devolvida';
                  const isCorrigida = revisao === 'corrigida';
                  const temHistorico = Number(gasto.ciclos_revisao) > 0;
                  const ehApp = isPrestacaoApp(gasto);

                  // OS-Tarefa: detectado pelo prefixo "[OS-Tarefa #...]" na descrição,
                  // gravado pelo lancarDespesaOSTarefaFechada no encerramento da OS.
                  const { isTarefa, osId, descricaoLimpa } = parseOSTarefa(gasto.descricao);

                  return (
                    <tr
                      key={gasto.id}
                      id={`gasto-row-${gasto.id}`}
                      className={auto ? 'cg-row--auto' : ''}
                    >
                      {/* Data */}
                      <td className="col-date td-date">
                        {formatDate(gasto.data_vencimento)}
                      </td>

                      {/* Descrição — anexos vêm na frente da frase, em grade 4×4 */}
                      <td className="td-desc">
                        <div className="cg-desc">
                          {/* [ANEXOS] Galeria de recibos ANTES do texto — suporta
                              múltiplos anexos da prestação (App) ou anexo único (web). */}
                          <AnexosGaleria
                            anexoUrl={gasto.anexo_url}
                            compact
                            titulo="Recibos"
                            onOpen={(item) => setAnexoParaVer(item)}
                          />

                          <div className="cg-desc__line">
                            {auto && (
                              <span className="cg-lock-badge" title="Registro automático de Frota">
                                <FaGasPump /> Auto
                              </span>
                            )}
                            {isTarefa && (
                              <span className="cg-lock-badge cg-lock-badge--tarefa" title={`Gerado ao encerrar a OS #${osId}`}>
                                <FaTools /> Tarefa OS
                              </span>
                            )}
                            <span className="cg-desc__text" title={gasto.descricao}>{descricaoLimpa}</span>
                          </div>
                        </div>
                      </td>

                      {/* Meta */}
                      <td className="col-meta td-meta">
                        <div className="cg-tags">
                          {gasto.categoria_nome && (
                            <span className="cg-tag cg-tag--cat">{gasto.categoria_nome}</span>
                          )}
                          {gasto.veiculo_placa && (
                            <span className="cg-tag cg-tag--car">{gasto.veiculo_placa}</span>
                          )}
                        </div>
                      </td>

                      {/* Valor */}
                      <td className="col-val td-val">
                        {formatCurrency(gasto.valor)}
                      </td>

                      {/* Status — com sobreposição da camada de revisão */}
                      <td className="col-status td-status">
                        {isDevolvida ? (
                          <span className="cg-badge cg-badge--devolvida" title="Aguardando correção do motorista">
                            Devolvida
                          </span>
                        ) : (
                          <span className={`cg-badge cg-badge--${sc.mod}`}>{sc.label}</span>
                        )}
                        {isCorrigida && (
                          <span
                            className="cg-badge cg-badge--corrigida"
                            title="Motorista corrigiu e reenviou — reavalie"
                          >
                            <FaReply /> Corrigida
                          </span>
                        )}
                      </td>

                      {/* Ações */}
                      <td className="col-actions td-actions">
                        <div className="cg-actions">
                          {/* Fluxo de aprovação */}
                          {gasto.status === 'Pendente' && (
                            <>
                              <button
                                className="cg-act cg-act--approve"
                                title="Aprovar"
                                onClick={() => handleUpdateStatus(gasto.id, 'Aprovada')}
                              >
                                <FaCheck />
                              </button>
                              <button
                                className="cg-act cg-act--reject"
                                title={ehApp ? 'Devolver ao motorista para correção' : 'Rejeitar'}
                                onClick={() => handleRejeitarClick(gasto)}
                              >
                                {ehApp ? <FaUndo /> : <FaTimes />}
                              </button>
                            </>
                          )}
                          {gasto.status === 'Aprovada' && (
                            <button
                              className="cg-act cg-act--pay"
                              title="Marcar como Paga"
                              onClick={() => handleUpdateStatus(gasto.id, 'Paga')}
                            >
                              <FaFileInvoiceDollar />
                            </button>
                          )}

                          {/* Histórico de revisões — só quando já houve ida-e-volta */}
                          {temHistorico && (
                            <button
                              className="cg-act cg-act--history"
                              title={`Ver histórico de revisões (${gasto.ciclos_revisao} ciclo${Number(gasto.ciclos_revisao) !== 1 ? 's' : ''})`}
                              onClick={() => setTimelineAlvo(gasto)}
                            >
                              <FaHistory />
                            </button>
                          )}

                          {/* Divider visual se houver botões de fluxo/histórico */}
                          {(gasto.status === 'Pendente' || gasto.status === 'Aprovada' || temHistorico) && (
                            <span className="cg-actions__divider" />
                          )}

                          {/* Editar */}
                          <button
                            className={`cg-act cg-act--edit ${auto ? 'cg-act--locked' : ''}`}
                            title={auto ? 'Registro automático — use o módulo de Abastecimento' : 'Editar'}
                            onClick={() => handleEditClick(gasto)}
                          >
                            {auto ? <FaLock /> : <FaEdit />}
                          </button>

                          {/* Excluir */}
                          <button
                            className={`cg-act cg-act--delete ${auto ? 'cg-act--locked' : ''}`}
                            title={auto ? 'Registro automático — exclusão bloqueada' : 'Excluir'}
                            onClick={() => handleDeleteGasto(gasto.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Rodapé com total (sobre a lista filtrada do mês) */}
            {!loading && gastosFiltrados.length > 0 && (
              <tfoot>
                <tr className="cg-table__foot">
                  <td colSpan={3}>
                    <span>{gastosFiltrados.length} registro{gastosFiltrados.length !== 1 ? 's' : ''} em {mesLabel}</span>
                  </td>
                  <td className="col-val">
                    <strong>{formatCurrency(kpis.total)}</strong>
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── PAGINAÇÃO ───────────────────────────────────────────────────────── */}
      {!loading && gastosFiltrados.length > 0 && (
        <div className="cg-pager">
          <span className="cg-pager__info">
            Mostrando <strong>{inicio + 1}</strong>–<strong>{Math.min(inicio + pageSize, gastosFiltrados.length)}</strong> de <strong>{gastosFiltrados.length}</strong>
          </span>

          <div className="cg-pager__spacer" />

          <label className="cg-pager__size">
            Por página
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {PAGE_SIZE_OPTS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>

          {totalPages > 1 && (
            <div className="cg-pager__controls">
              <button
                className="cg-pager__btn"
                onClick={() => setPage(1)}
                disabled={page === 1}
                title="Primeira página"
                aria-label="Primeira página"
              >
                <FaAngleDoubleLeft />
              </button>
              <button
                className="cg-pager__btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                title="Página anterior"
                aria-label="Página anterior"
              >
                <FaAngleLeft />
              </button>

              {pageWindow[0] > 1 && <span className="cg-pager__ellipsis">…</span>}

              {pageWindow.map((n) => (
                <button
                  key={n}
                  className={`cg-pager__btn ${n === page ? 'active' : ''}`}
                  onClick={() => setPage(n)}
                  aria-current={n === page ? 'page' : undefined}
                >
                  {n}
                </button>
              ))}

              {pageWindow[pageWindow.length - 1] < totalPages && <span className="cg-pager__ellipsis">…</span>}

              <button
                className="cg-pager__btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                title="Próxima página"
                aria-label="Próxima página"
              >
                <FaAngleRight />
              </button>
              <button
                className="cg-pager__btn"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                title="Última página"
                aria-label="Última página"
              >
                <FaAngleDoubleRight />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── MODAIS ──────────────────────────────────────────────────────────── */}
      {isModalOpen && (
        <GastoModal
          gasto={editingGasto}
          onClose={() => { setIsModalOpen(false); setEditingGasto(null); }}
          onSave={handleSaveGasto}
          categorias={categorias}
          veiculos={veiculos}
          userPerfil={currentUser?.perfil}
          onCategoriaCreated={handleCategoriaCreated}
        />
      )}
      {anexoParaVer && (
        <AnexoViewerModal
          anexoUrl={anexoParaVer}
          onClose={() => setAnexoParaVer(null)}
        />
      )}

      {/* Devolução de prestação ao motorista (motivo obrigatório + push) */}
      {devolverAlvo && (
        <ModalDevolverPrestacao
          gasto={devolverAlvo}
          onClose={() => setDevolverAlvo(null)}
          onDevolvida={handleDevolvida}
        />
      )}

      {/* Histórico de revisões (idas-e-voltas gestor ↔ motorista) */}
      {timelineAlvo && (
        <TimelineRevisoes
          gastoId={timelineAlvo.id}
          descricao={parseOSTarefa(timelineAlvo.descricao).descricaoLimpa}
          onClose={() => setTimelineAlvo(null)}
        />
      )}
    </div>
  );
}